<template>
  <template v-if="ctx">
    <!--
      The venue does both and no bill is open, so the mode is still the diner's:
      one control states it and offers the other. The table, once there is one,
      is the dine-in segment's label rather than a chip beside it — dining in is
      sitting at a table, and two objects were saying that once each.
    -->
    <PjModeToggle
      v-if="ctx.canSwitch"
      :dine-in-label="ctx.tableName"
      :model-value="ctx.mode"
      @update:model-value="chooseMode"
    />

    <!--
      Nothing to switch to. Seated, or at a venue that only seats — either way
      the table is the whole statement, and the chevron says the chip is a door:
      to the bill once there are rounds behind it, back to the picker while the
      table is still only this device's guess.
    -->
    <button
      v-else-if="ctx.tableName"
      class="flex min-h-9 items-center gap-1.5 rounded-full bg-container px-3.5 text-title text-on-container pj-tap hover:bg-surface-tint"
      type="button"
      @click="onTableClick"
    >
      <PjIcon
        :name="PJ_ICON.LOCATION"
        size="meta"
      />
      {{ ctx.tableName }}
      <PjIcon
        :name="PJ_ICON.CHEVRON_DOWN"
        size="meta"
      />
    </button>

    <!--
      A takeaway-only venue. There is no table to name and nothing to switch to,
      so this is a label, not a control — but it is said, which is what the slot
      used to fail to do here at all.
    -->
    <span
      v-else-if="ctx.mode === ORDER_MODE.TAKEAWAY"
      class="flex min-h-9 items-center gap-1.5 rounded-full bg-container px-3.5 text-title text-muted"
    >
      <PjIcon
        :name="PJ_ICON.TAKEAWAY"
        size="meta"
      />
      <!-- Hidden rather than dropped on a phone, where this shares a 64px row
           with the venue name and the account control. The accessible name
           survives at every width. -->
      <span :class="props.compact && 'max-sm:sr-only'">Takeaway</span>
    </span>

    <!--
      Dine-in only, with no table yet: the way in is the entire statement, so it
      carries the row on its own.
    -->
    <PjButton
      v-else-if="ctx.join"
      :aria-label="joinLabel"
      :class="props.compact && 'max-sm:aspect-square max-sm:px-0'"
      size="xs"
      variant="tonal"
      @click="join"
    >
      <PjIcon :name="ctx.join === JOIN_ACTION.PICK ? PJ_ICON.LOCATION : PJ_ICON.DINE_IN" />
      <span :class="props.compact && 'max-sm:hidden'">{{ joinLabel }}</span>
    </PjButton>
  </template>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { OrderContext } from '@/domains/pjazza/composables/use-order-context/useOrderContext.ts'
import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'

import PjButton from '@/domains/pjazza/components/atoms/pj-button/PjButton.vue'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import PjModeToggle from '@/domains/pjazza/components/molecules/pj-mode-toggle/PjModeToggle.vue'
import { JOIN_ACTION } from '@/domains/pjazza/composables/use-order-context/useOrderContext.ts'
import { ORDER_MODE } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'

/**
 * What this order is, and the way to change it — in one control.
 *
 * Every branch here is a row of the table `useOrderContext` resolves, and they
 * live together because the alternative is what this replaces: the same rule
 * derived in the shell and again on the menu page, in two spellings that
 * disagreed about a table which had been picked but not yet ordered onto.
 */
const props = defineProps<{
  context: OrderContext | null
  /** Sheds labels on a phone, where this shares a 64px row with the venue name. */
  compact?: boolean
}>()

const emit = defineEmits<{
  chooseMode: [OrderMode]
  pickTable: []
  manageTable: []
  scan: []
}>()

const ctx = computed(() => props.context)

const joinLabel = computed(() => {
  if (ctx.value?.join === JOIN_ACTION.PICK) return props.compact ? 'Your table' : 'Choose your table'

  return props.compact ? 'Scan' : 'Scan your table code'
})

const join = (): void => {
  if (ctx.value?.join === JOIN_ACTION.PICK) {
    emit('pickTable')

    return
  }

  emit('scan')
}

/**
 * Dine in means a table, so choosing it asks for one.
 *
 * That is the whole bet of this control: the diner taps the outcome they want
 * rather than a button naming the mechanism, and the app supplies the mechanism.
 * Tapping a table that is already named reopens the picker, so a mistyped table
 * is still correctable from here — which is what the chip's chevron used to do.
 */
const chooseMode = (next: OrderMode): void => {
  const wasActive = ctx.value?.mode === next

  emit('chooseMode', next)

  if (next !== ORDER_MODE.DINE_IN) return

  if (ctx.value?.join) {
    join()

    return
  }

  if (wasActive && ctx.value?.canChangeTable) emit('pickTable')
}

const onTableClick = (): void => {
  if (ctx.value?.canChangeTable) {
    emit('pickTable')

    return
  }

  emit('manageTable')
}
</script>
